import { ref } from 'vue';
import { endSse, sseSubscribeList } from '@/api/lock';
import { useUserStore, useSubscribeStore } from '@/store';
import { storeToRefs } from 'pinia';
import globalContants from '@/constants/globalContants';
import { BASE_URL } from '@/utils/global';
import { setHeaders } from '@/utils/request';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { v4 as uuidv4 } from 'uuid';
import { handleUnlockMessage } from '@/hooks/useSSE/common';

const useSSE = () => {
  const userStore = useUserStore();
  const subscribeStore = useSubscribeStore();
  const { userLoginInfo } = storeToRefs(userStore);
  const { subscribeList, isConnected, pingTimer, UUID } =
    storeToRefs(subscribeStore);
  const lastPushTimestamp = ref(new Date().getTime()); // 最后一次推消息的时间戳
  const pingGap = ref(globalContants.PING_MESSAGE_GAP);
  // 表示是否通过这次请求
  const byRequest = ref<boolean>(true);

  const start = async () => {
    if (UUID.value) {
      try {
        await endSSE();
      } finally {
        //
      }
    }

    // 连接中
    if (isConnected.value) return false;

    return new Promise<void>((resolve) => {
      try {
        const uuid = uuidv4();
        const clientId = `pc:${uuid}:${userLoginInfo.value?.id}`;
        const url = `${BASE_URL.scmServer}/api/sse/start.ac?clientId=${clientId}`;
        fetchEventSource(
          url,
          setHeaders(
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              openWhenHidden: true, // 在调用失败时禁止重复调用
              onopen() {
                // console.log('连接成功', response);
                setUUID(uuid);
                setConnectStatus(); // 标记为连接中
                resolve();
              },
              onmessage(event: any) {
                const message = JSON.parse(event.data);
                const { businessItem, data } = message;
                // console.log('message', data, businessItem);
                lastPushTimestamp.value = new Date().getTime(); // 重置最后一次推送时间戳
                if (businessItem === 'PING') {
                  if (pingGap.value !== data * 1000) {
                    pingGap.value = data * 1000; // 重置ping的间隔时时间
                    startPingTimer(); // 重置定时器，矫正pingGap
                  }
                  return;
                }
                console.log('message', businessItem, data);

                handleSSEMessage(message);
              },
              onerror(error: any) {
                setConnectStatus(false); // 标记为断连
                // console.log('error', error, clientId);
                throw error; // 异步捕获不到
              },
            },
            true
          )
        );
      } catch {
        //
      }
    });
  };
  const endSSE = () => {
    if (!isConnected.value) return false;
    const uuid = UUID.value;
    const uid = userLoginInfo.value?.id;
    setUUID();
    if (!uuid || !uid) return false;
    const clientId = `pc:${uuid}:${uid}`;
    return endSse(clientId);
  };

  const clearSSE = async () => {
    await endSSE();
    // resetInfo会清除pingTimer，所以要提前清除掉定时器
    closePingTimer();
    subscribeStore.resetInfo();
  };

  const checkSSEStatus = (isSync = false) => {
    if (isSync) initSSE();
    // 更新订阅列表
    getSubscribeList().then((list: string[]) => {
      if (isSync) return; // 开启了订阅则一定有订阅了
      if (list?.length) {
        initSSE();
      } else {
        closePingTimer();
        clearSSE();
      }
    });
  };

  const initSSE = () => {
    if (pingTimer.value) return;
    if (!UUID.value) {
      start().catch(() => {
        //
      });
    }
    startPingTimer();
  };

  const startPingTimer = () => {
    closePingTimer();
    const timerId = setInterval(async () => {
      // 超过一个gap没有ping，表示断连
      if (
        byRequest.value &&
        new Date().getTime() - lastPushTimestamp.value > pingGap.value + 500
      ) {
        byRequest.value = false;
        try {
          await endSSE();
        } finally {
          try {
            await start();
          } catch {
            //
          } finally {
            byRequest.value = true;
          }
        }
      }
    }, pingGap.value + 500); // 加0.5秒网络延迟
    setTimerId(timerId);
  };

  const closePingTimer = () => {
    if (!pingTimer.value) return;
    clearInterval(pingTimer.value);
    setTimerId();
  };

  // 标记连接状态
  const setConnectStatus = (status: boolean = true) => {
    subscribeStore.setConnectStatus(status);
  };

  const setUUID = (uuid: string = '') => {
    subscribeStore.setUUID(uuid);
  };

  const setTimerId = (timerId?: number) => {
    subscribeStore.setPingTimer(timerId);
  };

  // 处理message响应
  const handleSSEMessage = (message: Recordable) => {
    const { businessItem, data } = message;
    switch (businessItem) {
      case 'UNLOCK':
        // getSubscribeList(); // 更新订阅列表
        handleUnlockMessage(data); // 处理通知消息
        break;
      case 'UNSUBSCRIBE': {
        // 去除订阅key
        subscribeStore.removeSubscribeItems(data);
        if (!subscribeList.value?.length) {
          // 没有订阅，则断连
          clearSSE();
          closePingTimer();
        }
        break;
      }
      default:
    }
  };

  // 更新订阅列表
  const getSubscribeList = () => {
    return sseSubscribeList().then((res: any) => {
      if (res.data) {
        subscribeStore.setSubscribeList(res.data);
        return res.data;
      }
      return [];
    });
  };

  return {
    checkSSEStatus,
    clearSSE,
  };
};

export default useSSE;
